import React, { useEffect, useState, useRef } from "react";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Flex,
  Text,
  Input,
  LabelWrapper,
  TextArea
} from "@theorgchartdev/storybook";
import { SelectableOptionsMenu } from "@/appComponents/Blocks/Menus/SelectableOptionsMenu/SelectableOptionsMenu";
import SectionPrompt from "@/appComponents/Blocks/Composites/SectionPrompt/SectionPrompt";
import { GetWSMgr } from "@/Utils/StateLib";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { appstr } from "@coreapp/utils/appstr";
import { ShareWithHandler } from "../PlanBuilderModal/SharedWithHandler";
import { usePlanSubmission } from "@/hooks/usePlanSubmission";
import PlanDetailsSummaryCard from "@/appComponents/Blocks/Composites/PlanDetailsSummaryCard/PlanDetailsSummaryCard";
import {
  PlanInfoSection,
  PlanDetailsGrid
} from "@/appComponents/Blocks/Modals/PlanSubmissionDetails/PlanSubmissionDetails";

const MAX_APPROVERS = 3;

export const PlanSubmitModal = () => {
  const {
    planSubmitContextValue: { showSubmitModal, closeSubmitModal }
  } = usePlanManagerContext();

  const { submitPlanSubmission } = usePlanSubmission();

  const [reviewers, setReviewers] = useState([]);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sharedWithHandler, setSharedWithHandler] = useState(null);

  const newPlanUsers = useRef([]);

  useEffect(() => {
    if (!showSubmitModal) {
      setReviewers([]);
      setMessage("");
      setSearchQuery("");
      newPlanUsers.current = [];
      return;
    }

    const asyncHandler = async () => {
      const submitHandler = new ShareWithHandler();
      await submitHandler.loadUsersAndGroups();
      submitHandler.buildUserAndGroupOptions([], handleAddReviewer, true);
      setSharedWithHandler(submitHandler);
    };

    asyncHandler();
  }, [showSubmitModal]);

  if (!showSubmitModal) return null;

  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.fPlanManager;
  const planInfo = planMgr?.GetPlanInfo();

  const checkReviewersNeedAccess = newReviewers => {
    const wsMgr = GetWSMgr();
    const planMgr = wsMgr?.GetPlanMgr();
    const planInfo = planMgr?.GetPlanInfo();
    const planUsers = planInfo["users"] || [];
    const planUserIds = planUsers.map(planUser => planUser.name); // name maps to username for plan users
    return newReviewers.filter(
      reviewerCandidate => !planUserIds.includes(reviewerCandidate.username)
    );
  };

  const handleGrantPlanAccess = usersGrantedAccess => {
    newPlanUsers.current = [...newPlanUsers.current, ...usersGrantedAccess];
  };

  const handlePlanAccessAlert = requestReviewers => {
    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      appstr("Some approvers need access"),
      appstr("Some approvers need access info", {
        reviewersWithoutPlanAccessCount: requestReviewers.length,
        totalReviewerCount: reviewers.length
      }),
      () => {
        handleGrantPlanAccess(requestReviewers);
        handleSubmit(false);
      },
      () => {},
      appstr("Grant access"),
      appstr("Not now")
    );
  };

  const handleAddReviewer = users => {
    setReviewers(prevReviewers => {
      const newReviewers = users
        .filter(user => !prevReviewers.find(r => r.username === user.username))
        .map(user => ({ ...user, editorRole: "viewer" }));

      const updatedReviewers = [...prevReviewers, ...newReviewers];
      return updatedReviewers.length <= MAX_APPROVERS ? updatedReviewers : prevReviewers;
    });
  };

  const handleRemoveReviewer = username => {
    setReviewers(reviewers.filter(r => r.username !== username));
  };

  const handleSubmit = (checkReviewerPlanAccess = true) => {
    const usersWithoutPlanAccess = checkReviewersNeedAccess(reviewers);
    const showGrantAccessAlert = usersWithoutPlanAccess.length > 0;

    if (checkReviewerPlanAccess && showGrantAccessAlert) {
      handlePlanAccessAlert(usersWithoutPlanAccess);
      return;
    }

    const nextPlanUsers = getNextPlanUsers();

    submitPlanSubmission(reviewers, message, nextPlanUsers);
    closeSubmitModal();
  };

  const getNextPlanUsers = () => {
    const wsMgr = GetWSMgr();
    const planMgr = wsMgr?.fPlanManager;
    const planInfo = planMgr?.GetPlanInfo();
    let nextPlanUsers = [];
    if (newPlanUsers.current.length > 0) {
      const newUsers = newPlanUsers.current.map(user => ({ name: user.username, role: "viewer" }));
      nextPlanUsers = [...planInfo.users, ...newUsers];
    }
    return nextPlanUsers;
  };

  const onInput = inputValue => {
    setSearchQuery(inputValue);
  };

  const onResetInput = () => {
    setSearchQuery("");
  };

  const canSubmit = reviewers.length > 0 && reviewers.length <= MAX_APPROVERS;

  const reviewerOptions =
    sharedWithHandler?.getShareWithOptions(searchQuery || "", reviewers, true).map(option => ({
      ...option,
      disabled: option.disabled || reviewers.length >= MAX_APPROVERS
    })) ?? [];

  return (
    <CentralizedModalContainer show={showSubmitModal}>
      <Modal
        id="plan-submit-modal"
        data-testid="plan-submit-modal"
        show={showSubmitModal}
        onClose={closeSubmitModal}
        minWidth={"medium"}
        maxWidth={"medium"}
        withShadow={true}
        centerScreen={true}
      >
        <DialogTitle divider={true} onClose={closeSubmitModal}>
          {appstr("Submit plan")}
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content"}>
          <Flex direction={"column"} gap={"16px"} height={"100%"} width={"100%"}>
            <PlanDetailsSummaryCard>
              <PlanDetailsGrid>
                <PlanInfoSection planInfo={planInfo} />
              </PlanDetailsGrid>
            </PlanDetailsSummaryCard>

            <div className="oc-label-wrapper-no-margin">
              <LabelWrapper label={appstr("Select approvers for this plan")} require>
                <SelectableOptionsMenu
                  selectedItems={reviewers}
                  getItemId={r => r.username}
                  getItemLabel={r => r.name || r.email || r.username}
                  onAdd={user => handleAddReviewer([user])}
                  onRemove={reviewer => handleRemoveReviewer(reviewer.username)}
                  maxItems={MAX_APPROVERS}
                  options={reviewerOptions}
                  searchCallback={onInput}
                  placeholder={appstr("Search for approvers")}
                  expandable={reviewers.length > 1}
                  dataTestId="plan-submit-search-input"
                  onResetInput={onResetInput}
                  fullWidth
                />
                <Text size={"xsmall"} color={"secondary"} colorModifier={"grey"}>
                  {appstr("Maximum approvers", { approverLimit: MAX_APPROVERS })}
                </Text>
              </LabelWrapper>
            </div>
            <TextArea
              id="plan-submit-message"
              dataTestId="plan-submit-message"
              placeholder={appstr("Add a message for approvers")}
              value={message}
              onChange={e => setMessage(e.target.value)}
              charLimit={1000}
              rows={4}
              label={appstr("Submission message")}
            />
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary onClick={closeSubmitModal} data-testid="plan-submit-cancel-btn">
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={() => handleSubmit()}
            disabled={!canSubmit}
            data-testid="plan-submit-btn"
          >
            {appstr("Submit plan")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
